import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { MarketObjectFace } from "./Objects3D";
import { useBall } from "./ballThemes";
import { COCKPIT_MARKETS, COCKPIT_MARKET_IDS, fmtPrice } from "./cockpitMarkets";
import { reduceMotion } from "./fx";
import { hapticTick, hapticTap, hapticSelect } from "./haptics";
import { NumberTicker } from "../PerpsComponents/NumberAnimation";
import "./cockpit.css";

/**
 * The Collectibles switcher, laid out as an open Pokédex.
 *
 * The device is the display: a 3D coverflow of the card markets. Drag, use the
 * ‹ › arrows or the keyboard to spin, and tap the centred card to trade it.
 *
 * The readout plate that used to sit beside it — name, price, change, stats,
 * description and a trade button — is gone, as it is in the MarketSelector.
 * Browsing is the job here; the trading page it hands off to owns the numbers.
 *
 * Opens when a trader picks the "Collectibles" tab in the market dropdown.
 *
 * Picking any other category tab hands control back to the regular dropdown
 * list via `onSelectCategory` — only Collectibles uses the coverflow.
 *
 * @param {object} props
 * @param {string} [props.currentId] - Market id to start centred on
 * @param {Array<{key: string, label: string}>} props.categories - Tabs to show
 * @param {Object<string, {price?: number, change24h?: number}>} [props.prices] - Live prices by market id
 * @param {Function} props.onSelect - (marketId) => void, fired when a market is chosen
 * @param {Function} props.onSelectCategory - (categoryKey) => void, for non-collectible tabs
 * @param {DOMRect} [props.anchorRect] - Viewport rect of the market button in
 *   the trading header. The device grows out of it and folds back into it, the
 *   same move the MarketSelector makes. Omit it and it simply fades.
 * @param {boolean} [props.enterInPlace] - True when the MarketSelector handed
 *   over to this rather than the trading page opening it, so it fades in where
 *   it stands instead of growing out of the market button.
 * @param {Function} props.onClose - Close the overlay
 */
/** How long the device takes to grow out of, or fold back into, the button. */
const COLLAPSE_MS = 380;
/**
 * The swap between this and the MarketSelector is not animated at all.
 *
 * Switching browsers is switching category — the same act as Real Estate to
 * Watches inside the selector, which simply redraws. Both carry the same
 * search field and tab strip in the same places, so anything played over that
 * swap, even a crossfade, shows as those shared controls flickering while the
 * layer under them changes. The objects arriving on the stage are the
 * animation.
 */

export function MarketSwitcher({
  currentId,
  categories = [],
  prices = {},
  onSelect,
  onSelectCategory,
  anchorRect,
  enterInPlace = false,
  onClose,
}) {
  const ball = useBall();
  const [query, setQuery] = useState("");

  // Phones get a tighter coverflow, as the selector does. At 240 the second
  // card sits half off a ~300px display, and the arrows land on top of it.
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  /**
   * The display's own height caps the cards.
   *
   * They used to be a flat 168px wide (118 on phones), tuned when the device
   * owned the screen. As a dropdown the display can be half that tall, and a
   * fixed card size there does not stay legible — it just runs out through the
   * bottom edge. The cards are portrait at roughly 1.4:1, so the height budget
   * converts straight into a width.
   */
  const displayRef = useRef(null);
  const [displayH, setDisplayH] = useState(0);
  useEffect(() => {
    const el = displayRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      setDisplayH((prev) => (prev === h ? prev : h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cardW = useMemo(() => {
    const cap = narrow ? 118 : 168;
    if (!displayH) return cap;
    // The centred card is drawn slightly large and carries a name plate under
    // it; the margin is what keeps both inside the display.
    return Math.max(78, Math.min(cap, (displayH - 74) / 1.4));
  }, [narrow, displayH]);
  // Seats follow the card, so shrinking one closes the other up with it.
  const spacing = useMemo(() => {
    const cap = narrow ? 132 : 240;
    return Math.max(88, Math.min(cap, cardW * (narrow ? 1.12 : 1.43)));
  }, [narrow, cardW]);

  /**
   * The device is the trading page's market button, opened out — the same
   * move the MarketSelector makes, so the two browsers arrive and leave the
   * same way. `transform-origin: 0 0` with a uniform scale of `button width /
   * device width` lands the shrunken device exactly on the button; scaling
   * each axis to fit would stretch the casing on the way.
   */
  const [shown, setShown] = useState(enterInPlace);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (enterInPlace) return undefined;
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
    // Only ever the mount: how it arrived cannot change under it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const open = shown && !closing;

  /**
   * The device hangs off the market button like a dropdown, the same way the
   * MarketSelector does — the trader stays on the chart, and the panel stays
   * attached to the control that opened it. Clamped to the viewport on both
   * axes; below 900px it fills the screen, where nothing can hang off anything.
   */
  const [vp, setVp] = useState(() => ({
    w: typeof window === "undefined" ? 1440 : window.innerWidth,
    h: typeof window === "undefined" ? 900 : window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const box = useMemo(() => {
    const pad = 12;
    const w = Math.min(1180, vp.w - pad * 2);
    const small = vp.w < 900;
    if (!anchorRect || small) {
      const h = Math.min(760, vp.h - pad * 2);
      return { left: Math.round((vp.w - w) / 2), top: Math.round(small ? pad : (vp.h - h) / 2), width: w, height: h };
    }
    const top = Math.round(Math.min(anchorRect.bottom + 10, Math.max(64, vp.h - 320)));

    /* Takes the trading page's chart column — its left edge, its width — and
       stops short of the order rail, off the same row constants as the
       MarketSelector. See the long note there. */
    const rail = vp.w >= 1280 ? 400 : 340;
    const row = Math.min(1600, vp.w * 0.92);
    const left = Math.round(Math.max(pad, (vp.w - row) / 2));
    const width = Math.max(660, Math.min(w, Math.round(row - rail - 20)));

    return { left, top, width, height: Math.min(720, vp.h - top - pad) };
  }, [anchorRect, vp]);

  /**
   * Stays parked under the market button when the page scrolls instead of
   * riding down the viewport with it — the same offset the MarketSelector
   * applies, and for the same reason. See the note there.
   */
  const [scrolled, setScrolled] = useState(0);
  useEffect(() => {
    if (!anchorRect) return undefined;
    const x = Math.min(vp.w - 1, Math.max(0, anchorRect.left + anchorRect.width / 2));
    const y = Math.min(vp.h - 1, Math.max(0, anchorRect.top + anchorRect.height / 2));
    const base = new Map();
    for (const el of document.elementsFromPoint(x, y)) {
      if (el.scrollHeight > el.clientHeight + 1) base.set(el, el.scrollTop);
    }
    const baseWin = window.scrollY;
    const onScroll = () => {
      let d = window.scrollY - baseWin;
      base.forEach((from, el) => { d += el.scrollTop - from; });
      setScrolled((prev) => (prev === d ? prev : d));
    };
    // Capture — scroll does not bubble, so the column's own scroll would
    // otherwise never reach the document.
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRect]);

  /**
   * Where the device landed, so the collapsed pose can be expressed relative to
   * it now that it is a centred panel rather than the whole screen. Read off
   * the *layout* box — `offsetLeft/Top/Width`, not `getBoundingClientRect()` —
   * because the device already carries the collapsed transform on the frame
   * this runs, and the measured rect would fold that transform into itself.
   * The offset parent is the backdrop, pinned to 0,0, so these are viewport
   * coordinates.
   */
  const deviceRef = useRef(null);
  const [collapsed, setCollapsed] = useState("scale(.96)");
  useLayoutEffect(() => {
    const el = deviceRef.current;
    if (!anchorRect || !el || !el.offsetWidth) return;
    setCollapsed(
      `translate(${anchorRect.left - el.offsetLeft}px, ${anchorRect.top - el.offsetTop}px) scale(${anchorRect.width / el.offsetWidth})`
    );
  }, [anchorRect]);

  /**
   * Everything that dismisses the device plays a move first.
   *
   * @param {Function} done - What to do once the move has played
   * @param {boolean} [handoff] - Leaving for the selector, not the page
   */
  const closeAfterCollapse = useCallback((done, handoff = false) => {
    if (handoff) { done(); return; }
    setClosing(true);
    window.setTimeout(done, COLLAPSE_MS);
  }, []);
  const requestClose = useCallback(() => closeAfterCollapse(onClose), [closeAfterCollapse, onClose]);

  /**
   * Dismiss on a click outside the device — the backdrop no longer catches it.
   * The flat market list renders over this rather than inside it, and the
   * market button owns what a click on itself means, so neither counts as
   * outside. Mirrors MarketSelector.
   */
  useEffect(() => {
    const onDocDown = (e) => {
      if (deviceRef.current?.contains(e.target)) return;
      if (e.target.closest?.("[data-market-dropdown], .ce-market-btn")) return;
      const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
      window.addEventListener("click", swallow, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 0);
      requestClose();
    };
    document.addEventListener("mousedown", onDocDown, true);
    return () => document.removeEventListener("mousedown", onDocDown, true);
  }, [requestClose]);
  const pick = useCallback(
    (id) => { hapticSelect(); closeAfterCollapse(() => onSelect(id)); },
    [closeAfterCollapse, onSelect]
  );

  /** The two cockpit markets, decorated with whatever live price we have. */
  const list = useMemo(() => {
    const base = COCKPIT_MARKET_IDS.map((id) => {
      const p = prices[id] || {};
      return {
        ...COCKPIT_MARKETS[id],
        price: p.price != null ? Number(p.price) : null,
        change: p.change24h != null ? Number(p.change24h) : 0,
      };
    });
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((m) => `${m.name} ${m.short} ${m.id}`.toLowerCase().includes(q));
  }, [prices, query]);

  const startIdx = Math.max(0, list.findIndex((x) => x.id === currentId));
  const [pos, setPos] = useState(startIdx < 0 ? 0 : startIdx);
  const [drag, setDrag] = useState(false);
  const posRef = useRef(pos);
  posRef.current = pos;

  // sliding gradient pill behind the active category tab
  const tabRefs = useRef({});
  const [pill, setPill] = useState(null);
  useLayoutEffect(() => {
    const el = tabRefs.current.rwa;
    if (el) setPill({ left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight });
  }, [categories]);

  useEffect(() => {
    setPos(0);
  }, [query]);

  const clamp = (p) => Math.max(0, Math.min(list.length - 1, p));
  const center = Math.round(clamp(pos));

  const nudge = (d) => {
    hapticTick();
    setPos((p) => clamp(Math.round(p) + d));
  };

  const onDown = (e) => {
    setDrag(true);
    const startX = e.clientX;
    const startPos = posRef.current;
    const move = (ev) => setPos(clamp(startPos - (ev.clientX - startX) / spacing));
    const up = () => {
      setDrag(false);
      setPos((p) => clamp(Math.round(p)));
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") nudge(1);
      else if (e.key === "ArrowLeft") nudge(-1);
      else if (e.key === "Enter" && list[center]) pick(list[center].id);
      else if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, center, pick, requestClose]);

  const reduce = reduceMotion();


  return (
    <div
      className="ce-dex-backdrop"
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        // Passes under the app header on the way out rather than over it, as
        // the MarketSelector's does.
        clipPath: "inset(var(--app-header-h, 68px) 0 0 0)",
        // No tint, and no events: the page behind stays live and scrollable,
        // and a scrim over a live page says the opposite. Dismissal moves to
        // the document listener above, as in MarketSelector.
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
    <div
      ref={deviceRef}
      className="ce-cockpit ce-raw ce-switcher"
      style={{
        position: "absolute",
        left: box.left, top: box.top - scrolled, width: box.width, height: box.height,
        background: "var(--pd-red)", overflow: "hidden",
        boxShadow: "0 30px 80px rgba(16,24,40,.36)",
        transformOrigin: "0 0",
        transform: reduce || open ? "none" : collapsed,
        opacity: open ? 1 : 0,
        borderRadius: open ? 20 : 16,
        transition: reduce
          ? `opacity ${Math.round(COLLAPSE_MS * 0.5)}ms linear`
          : `transform ${COLLAPSE_MS}ms cubic-bezier(.4,0,.2,1), opacity ${Math.round(COLLAPSE_MS * 0.6)}ms linear, border-radius ${COLLAPSE_MS}ms linear`,
        willChange: "transform",
        // The one live layer inside a backdrop that no longer takes events.
        pointerEvents: "auto",
      }}
    >
      {/* Casing shading — lit top-left, deepening at the edges. */}
      <span className="ce-dex-shade" aria-hidden="true" />

      <div className="ce-dex-page">
        {/* The device head — big lens, lamps, casing ridge, hinge barrels, the
            ball loader and ✕ — used to open this panel. It is gone for the same
            reason the MarketSelector's wordmark and headline are: as a dropdown
            hanging off the market button there is no room to spend on saying
            what this is, and every pixel it took is a pixel the cards did not
            have. The casing itself still reads as the Pokédex. Dismissal is
            Escape, a click outside, or the market button. */}
        {/* ── Search + category tabs ── */}
        <div className="ce-dex-controls">
          {/* Search is the "I know what I want, skip the spinning" path. It
              filters this device's own coverflow and list — the flat market
              list that used to open over it is gone. */}
          <div className="ce-dex-searchrow">
            <div className="ce-dex-search">
              <span aria-hidden="true">🔍</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search markets — name or ticker"
                aria-label="Search markets"
              />
              {query && <button onClick={() => setQuery("")} aria-label="Clear search">✕</button>}
            </div>
          </div>

          <div className="ce-dex-tabs">
            {categories.map((cc) => {
              const on = cc.key === "rwa";
              return (
                <button
                  key={cc.key}
                  ref={(el) => { tabRefs.current[cc.key] = el; }}
                  onClick={() => (on ? null : closeAfterCollapse(() => onSelectCategory(cc.key), true))}
                  className={on ? "ce-dex-tab ce-dex-tab-on" : "ce-dex-tab"}
                >
                  {cc.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── The display ── */}
        <div className="ce-dex-body">
          <div className="ce-dex-plate ce-dex-plate-left">
            {/* Category + counter flank the lamps, matching the row the
                MarketSelector puts above its display. */}
            <div className="ce-dex-plate-head">
              {/* Label and lamps travel together as one side column, so the
                  name between the two columns sits on the plate's centre line
                  rather than centring in whatever space is left over. */}
              <span className="ce-dex-plate-side">
                <span className="ce-dex-plate-label">Collectibles</span>
                <span className="ce-dex-lamps" aria-hidden="true">
                  <span className="ce-dex-lamp-sm ce-dex-lamp-red" />
                  <span className="ce-dex-lamp-sm ce-dex-lamp-yellow" />
                  <span className="ce-dex-lamp-sm ce-dex-lamp-green" />
                </span>
              </span>
              {/* The centred card's name, in the head rather than over the
                  stage — same place the MarketSelector puts it, and for the
                  same reason: as a popup the display is short enough that a
                  tall card reaches the band the name used to own. */}
              <span className="ce-dex-stage-name">{list[center] ? list[center].name : ""}</span>
              <span className="ce-dex-count">{list.length ? center + 1 : 0} of {list.length}</span>
            </div>

            <div className="ce-dex-display" ref={displayRef} onPointerDown={onDown} style={{ cursor: drag ? "grabbing" : "grab" }}>

              <div className="ce-dex-stage">
                {list.map((m, i) => {
                  const off = i - pos;
                  const abs = Math.abs(off);
                  if (abs > 3.4) return null;
                  const isCenter = Math.round(pos) === i;
                  return (
                    <div
                      key={m.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isCenter) { pick(m.id); }
                        else { hapticTap(); setPos(clamp(i)); }
                      }}
                      className="ce-dex-card"
                      style={{
                        transform: `translate(-50%,-50%) translateX(${off * spacing}px) translateZ(${-abs * 220}px) rotateY(${Math.max(-52, Math.min(52, off * -30))}deg) scale(${isCenter ? 1.06 : 0.92})`,
                        transition: drag ? "none" : "transform .45s cubic-bezier(.22,1,.36,1)",
                        zIndex: 100 - Math.round(abs),
                        opacity: abs > 2.6 ? 0 : 1,
                        filter: isCenter ? "none" : "brightness(0.78)",
                      }}
                    >
                      <MarketObjectFace m={m} w={cardW} />
                    </div>
                  );
                })}
              </div>

              {list.length === 0 && (
                <div className="ce-dex-empty">No collectible markets match “{query}”.</div>
              )}

              {list.length > 1 && (
                <>
                  <button className="ce-dex-arrow ce-dex-arrow-l" onClick={(e) => { e.stopPropagation(); nudge(-1); }} aria-label="Previous market">‹</button>
                  <button className="ce-dex-arrow ce-dex-arrow-r" onClick={(e) => { e.stopPropagation(); nudge(1); }} aria-label="Next market">›</button>
                </>
              )}

              {/* Position dots — same strip as the MarketSelector's. */}
              <div className="ce-dex-dots">
                {list.map((m, i) => (
                  <span
                    key={m.id}
                    role="button"
                    aria-label={`Show ${m.name}`}
                    className={i === center ? "ce-dex-dot ce-dex-dot-on" : "ce-dex-dot"}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); hapticTap(); setPos(i); }}
                  />
                ))}
              </div>
            </div>

            <div className="ce-dex-plate-foot" aria-hidden="true">
              <span className="ce-dex-knob" />
              <span className="ce-dex-vent"><i /><i /><i /><i /></span>
            </div>
          </div>

          {/* The same markets as a list, beside the display — the column the
              MarketSelector carries, in device chrome, so the two browsers are
              the same shape. Hovering a row spins the display to that card;
              clicking trades it. */}
          <div className="ce-dex-plate ce-dex-listcol">
            <div className="ce-dex-plate-head">
              <span className="ce-dex-plate-label">Collectibles</span>
              <span className="ce-dex-count">{list.length} {list.length === 1 ? "market" : "markets"}</span>
            </div>

            <div className="ce-dex-lscroll">
              {list.map((m, i) => {
                const chg = Number(m.change || 0);
                return (
                  <button
                    key={m.id}
                    type="button"
                    className="ce-dex-lrow"
                    data-on={i === center ? "1" : undefined}
                    onMouseEnter={() => setPos(i)}
                    onClick={() => pick(m.id)}
                    aria-current={i === center ? "true" : undefined}
                  >
                    <i className="ce-dex-chip-dot" style={{ background: m.accent }} aria-hidden="true" />
                    <span className="ce-dex-lrow-id">
                      <b>{m.short}</b>
                      <span>{m.name}</span>
                    </span>
                    <span className="ce-dex-lrow-px">
                      <b><NumberTicker text={fmtPrice(m.price)} duration={0.5} stagger={0.025} /></b>
                      <span data-dn={chg < 0 ? "1" : undefined}>
                        <NumberTicker text={`${chg >= 0 ? "+" : ""}${chg.toFixed(2)}%`} duration={0.5} stagger={0.025} />
                      </span>
                    </span>
                  </button>
                );
              })}

              {list.length === 0 && (
                <div className="ce-dex-lempty">Nothing matches “{query}”.</div>
              )}
            </div>
          </div>

        </div>

        {/* Hint line. The ticker rail that ran above it is gone, as it is in
            the MarketSelector: as a dropdown there is no height to spend on a
            marquee of two markets that are already on the stage. */}
        <div className="ce-dex-foot">
          <div className="ce-dex-hint">🖐 Drag the display to spin · tap the centred card to trade · {ball.name} loaded</div>
        </div>
      </div>
    </div>
    </div>
  );
}

export default MarketSwitcher;
